import { useState } from 'react';
import {
    Box, Card, CardHeader, CardContent, Typography, TextField,
    IconButton, Button, Avatar, Stack, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { AVATAR_BG, AVATAR_FG, initials } from './constants';

const BLUE = '#004df2';
const EMPTY_CONTACT = { name: '', email: '', title: '', phone: '' };
const cardSx = { borderRadius: 3 };

// Who to talk to about THIS provider — hub-only, shared across every client
// that has it (e.g. the vendor's own account rep or support contact), and
// deliberately separate from the CS ClientOverview keyContacts field (that
// one is a specific client's overall CS relationship).

function ContactForm({ form, setForm, onSave, onCancel }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1.25 }}>
                {[
                    ['name', 'Name'], ['title', 'Job Title'], ['email', 'Email'], ['phone', 'Phone'],
                ].map(([field, label]) => (
                    <TextField key={field} size="small" label={label} value={form[field] || ''}
                        onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button size="small" variant="contained" onClick={onSave} disabled={!form.name?.trim()}
                    sx={{ textTransform: 'none', backgroundColor: BLUE, borderRadius: 2, '&:hover': { backgroundColor: '#0040c8' } }}>
                    Save
                </Button>
            </Box>
        </Box>
    );
}

const KeyContacts = ({ contacts, onChange }) => {
    const [editing, setEditing] = useState(null); // 'new' | index | null
    const [form, setForm] = useState(EMPTY_CONTACT);

    const list = contacts || [];

    const save = () => {
        if (!form.name?.trim()) return;
        onChange(editing === 'new' ? [...list, form] : list.map((c, i) => (i === editing ? form : c)));
        setEditing(null);
    };

    return (
        <Card variant="outlined" sx={cardSx}>
            <CardHeader
                avatar={<Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34 }}><PersonOutlineIcon sx={{ fontSize: 19 }} /></Avatar>}
                title={<Typography sx={{ fontWeight: 700, fontSize: 15 }}>Key Contacts</Typography>}
                subheader={<Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Hub-only, shared across every client on this provider.</Typography>}
                action={editing === null && (
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                        onClick={() => { setForm(EMPTY_CONTACT); setEditing('new'); }}
                        sx={{ textTransform: 'none', color: BLUE, mt: 1 }}>
                        Add contact
                    </Button>
                )}
                sx={{ pb: 0.5 }}
            />
            <CardContent sx={{ pt: 0.5 }}>
                {list.map((c, i) => (
                    <Box key={`${c.email || c.name}-${i}`}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
                            <Avatar sx={{ bgcolor: AVATAR_BG, color: AVATAR_FG, width: 34, height: 34, fontSize: 13, fontWeight: 700 }}>
                                {initials(c.name)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{c.name}</Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {c.title || '—'}
                                    {c.email && (
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, ml: 0.75 }}>
                                            <EmailOutlinedIcon sx={{ fontSize: 12 }} />
                                            <Box component="a" href={`mailto:${c.email}`} sx={{ color: BLUE, textDecoration: 'none' }}>{c.email}</Box>
                                        </Box>
                                    )}
                                    {c.phone && (
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, ml: 0.75 }}>
                                            <PhoneOutlinedIcon sx={{ fontSize: 12 }} />
                                            <Box component="a" href={`tel:${c.phone}`} sx={{ color: BLUE, textDecoration: 'none' }}>{c.phone}</Box>
                                        </Box>
                                    )}
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => { setForm({ ...EMPTY_CONTACT, ...c }); setEditing(i); }}>
                                <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => onChange(list.filter((_, x) => x !== i))}>
                                <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Stack>
                        {i < list.length - 1 && <Divider />}
                    </Box>
                ))}

                {editing !== null && (
                    <ContactForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditing(null)} />
                )}

                {list.length === 0 && editing === null && (
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 1 }}>No key contacts yet.</Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default KeyContacts;
